import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessAnalyticsGear } from '@shared/lib';
import { Header } from '@widgets/header';

export const Route = createFileRoute('/$locale/_authenticated/warehouse')({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessAnalyticsGear(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects are thrown response objects.
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: WarehouseLayout,
});

function WarehouseLayout() {
  const { locale } = Route.useParams();
  const navigate = Route.useNavigate();
  const auth = useAuth();
  const ability = useAbility();
  const canRead = canAccessAnalyticsGear(ability);

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && !canRead) {
      void navigate({ to: '/$locale/unauthorized', params: { locale }, replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, canRead, locale, navigate]);

  if (auth.isLoading || !auth.isAuthenticated || !canRead) return null;

  return (
    <div className="flex min-h-full flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />
      <main className="min-w-0 p-4 sm:p-6">
        <Outlet />
      </main>
    </div>
  );
}
